import { useState, useCallback, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

export interface UserTOCEntry {
  name: string
  children?: UserTOCEntry[]
}

interface UserTOC {
  entries: UserTOCEntry[]
}

export const useUserTOC = (customer: string, job: string) => {
  const [entries, setEntries] = useState<UserTOCEntry[]>([])
  const [loaded, setLoaded] = useState(false)
  // Use ref to avoid stale closures in callbacks
  const entriesRef = useRef<UserTOCEntry[]>([])

  const load = useCallback(async () => {
    if (!customer || !job) return
    try {
      const res = await axios.get<UserTOC>(`${API_BASE_URL}/tables/${customer}/${job}/user-toc`)
      const e = res.data.entries || []
      setEntries(e)
      entriesRef.current = e
      setLoaded(true)
    } catch {
      setEntries([])
      entriesRef.current = []
      setLoaded(true)
    }
  }, [customer, job])

  // A failed write throws, so the caller shows it; the copy here goes back to
  // the last one the server had, and the next write does not undo anything.
  const save = useCallback(async (newEntries: UserTOCEntry[]) => {
    if (!customer || !job) return
    const before = entriesRef.current
    setEntries(newEntries)
    entriesRef.current = newEntries
    try {
      await axios.put(`${API_BASE_URL}/tables/${customer}/${job}/user-toc`, { entries: newEntries })
    } catch (err) {
      setEntries(before)
      entriesRef.current = before
      throw err
    }
  }, [customer, job])

  // A whole-file write starts from the server's copy, read just before it:
  // the agent and the server routes (save, remove, rename, move) also write
  // user.toc, and an older copy written back would undo them. Unlike load(),
  // a failed read throws, so nothing is written from an empty list.
  const fresh = useCallback(async (): Promise<UserTOCEntry[]> => {
    const res = await axios.get<UserTOC>(`${API_BASE_URL}/tables/${customer}/${job}/user-toc`)
    const e = res.data.entries || []
    setEntries(e)
    entriesRef.current = e
    return e
  }, [customer, job])

  /** Swap an entry up or down within its parent list (root or inside a folder) */
  const moveEntry = useCallback(async (name: string, direction: 'up' | 'down', allTableNames?: string[]) => {
    const current = await fresh()
    let tree = current.length > 0
      ? structuredClone(current)
      : (allTableNames || []).map(n => ({ name: n } as UserTOCEntry))

    // Try to move in a list — returns true if found and moved
    const moveInList = (list: UserTOCEntry[]): boolean => {
      const idx = list.findIndex(e => e.name === name)
      if (idx !== -1) {
        const newIdx = direction === 'up' ? idx - 1 : idx + 1
        if (newIdx < 0 || newIdx >= list.length) return true // found but at boundary
        const [moved] = list.splice(idx, 1)
        list.splice(newIdx, 0, moved)
        return true
      }
      // Search inside folders
      for (const e of list) {
        if (Array.isArray(e.children) && moveInList(e.children)) return true
      }
      return false
    }

    moveInList(tree)
    await save(tree)
  }, [fresh, save])

  /**
   * Add an empty folder. parentPath is a My Tables folder path relative to
   * Tables/User/<email> ('A/B'), '' for the top level. A parent missing from
   * user.toc is added on the way. An empty folder lives only in user.toc.
   */
  const createFolder = useCallback(async (folderName: string, parentPath = '') => {
    const tree = structuredClone(await fresh())
    let list = tree
    for (const part of parentPath.split('/').filter(Boolean)) {
      let folder = list.find(e => e.name === part && Array.isArray(e.children))
      if (!folder) {
        folder = { name: part, children: [] }
        list.push(folder)
      }
      list = folder.children!
    }
    list.push({ name: folderName, children: [] })
    await save(tree)
  }, [fresh, save])

  const deleteFolder = useCallback(async (folderName: string) => {
    const newEntries = (await fresh()).filter(
      e => !(e.name === folderName && Array.isArray(e.children))
    )
    await save(newEntries)
  }, [fresh, save])

  // Moving and renaming a table are server routes (tocApi.moveUserTable): an
  // edit here alone would leave the blob where it was, and the tree shows a
  // blob with no entry at the top of My Tables.
  const folders = entriesRef.current
    .filter(e => Array.isArray(e.children))
    .map(e => e.name)

  return {
    entries,
    loaded,
    folders,
    load,
    save,
    moveEntry,
    createFolder,
    deleteFolder,
  }
}

export default useUserTOC
